import type { ClientPlugin } from 'fets';

export type ApiLogEntry = {
  id: number;
  timestamp: string;
  method: string;
  path: string;
  request?: {
    json?: unknown;
    formData?: Record<string, string>;
    formUrlEncoded?: unknown;
    params?: unknown;
    query?: unknown;
  };
  response?: {
    status: number;
    ok: boolean;
    durationMs: number;
    errorText?: string;
  };
};

class FifoCache<K, V> {
  private map = new Map<K, V>();
  constructor(private maxSize: number = 20) {}

  set(key: K, value: V): boolean {
    const isUpdate = this.map.has(key);
    if (isUpdate) {
      this.map.delete(key); // remove existing entry to move to end
    }
    this.map.set(key, value);

    // Evict oldest entry if cache exceeds max size
    if (this.map.size > this.maxSize) {
      const oldestKey = this.map.keys().next().value!;
      this.map.delete(oldestKey);
    }

    return isUpdate;
  }

  dump(): string {
    if (this.map.size === 0) return '(empty)';
    const entries = [...this.map.entries()];
    return entries
      .map(([key, value], i) => `[${i + 1}] ${JSON.stringify(key)}: ${JSON.stringify(value, null, 2)}`)
      .join('\n\n');
  }
}

export const global_log_cache = new FifoCache<number, ApiLogEntry>(10);

export function LogPlugin(cache: FifoCache<number, ApiLogEntry>): ClientPlugin {
  let nextId = 1;

  return {
    async onResponse({ response, requestParams, method, path }) {
      const entry: ApiLogEntry = {
        id: nextId++,
        timestamp: new Date().toISOString(),
        method,
        path,
      };

      const req: NonNullable<ApiLogEntry['request']> = {};
      if (requestParams?.json !== undefined) req.json = requestParams.json;
      if (requestParams?.formData) {
        const loggable = Object.entries(requestParams.formData).reduce((acc, [k, v]) => {
          if (!(v instanceof Blob)) acc[k] = v;
          return acc;
        }, {} as Record<string, string>);
        if (Object.keys(loggable).length > 0) req.formData = loggable;
      }
      if (requestParams?.formUrlEncoded) req.formUrlEncoded = requestParams.formUrlEncoded;
      if (requestParams?.params) req.params = requestParams.params;
      if (requestParams?.query !== undefined) req.query = requestParams.query;
      if (Object.keys(req).length > 0) entry.request = req;

      const resp: NonNullable<ApiLogEntry['response']> = {
        status: response.status,
        ok: response.ok,
        durationMs: 0,
      };
      if (!response.ok) {
        resp.errorText = await response.clone().text();
      }
      entry.response = resp;
      cache.set(entry.id, entry);
    },
  };
}
